import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration"; // Importing nothing to trigger the side effects, which in this case adds custom reducers.
import { setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { TypographyProps } from "@insite/mobius/Typography";
import { css } from "styled-components";

// Arlanet: this is needed for loading custom state
const store = require.context("./Store", true);
store.keys().forEach(key => store(key));
if (module.hot) {
    module.hot.accept(store.id, () => onHotWidgetReplace(require.context("./Store", true)));
}
// End

// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
const widgets = IS_PRODUCTION
    ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
    : require.context("./Widgets", true, /\.tsx$/);
// enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// load all pages. Without this they won't be included in the bundle
const pages = require.context("./Pages", true, /\.tsx$/);
const onHotPageReplace = addPagesFromContext(pages);
if (module.hot) {
    module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
}

// load all handlers. They could be loaded individually instead.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// in production, the widget extensions are included in the chunks created for widgets
if (!IS_PRODUCTION) {
    // load all widget extensions. They could be loaded individually instead
    const widgetExtensions = require.context("./WidgetExtensions", true);
    widgetExtensions.keys().forEach(key => widgetExtensions(key));
}

// add some pre styleguide customizations. These can be overridden by the style guid.

const buttonTypographyProps: TypographyProps = {
    fontFamily: "Pancetta W05 Bold Italic, sans-serif",
    size: "16px",
    italic: true,
    weight: "bold",
};

setPreStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 1024, 1441],
        maxWidths: [540, 540, 920, 1024, 1440],
    },
    toast: {
        defaultProps: {
            cssOverrides: {
                toast: css`
                    /* Hide Spire internal toasts */
                    display: none;
                `,
            },
        },
    },
    colors: {
        common: {
            background: "#FFFFFF",
            backgroundContrast: "#363636",
            accent: "#F8F9FA",
            accentContrast: "#363636",
            border: "#00000026",
            disabled: "#666666",
        },
        primary: {
            main: "#e30613",
            contrast: "#FFFFFF",
        },
        secondary: {
            main: "#0679E3",
            contrast: "#FFFFFF",
        },
        text: {
            link: "#000000",
            main: "#000000",
            disabled: "#475353",
            accent: "#2D3435",
        },
        success: {
            main: "#67E306",
            contrast: "#FFFFFF",
        },
        danger: {
            main: "#e30613",
            contrast: "#FFFFFF",
        },
        warning: {
            main: "#B91918",
            contrast: "#FFFFFF",
        },
        info: {
            main: "#ABABAB",
            contrast: "#FFFFFF",
        },
    },
    focus: {
        color: "string",
        style: "string",
        width: "string",
    },
    globalStyle: {
        css: css``,
    },
    typography: {
        fontFamilyImportUrl: "/staticContent/stylesheet.css",
        body: {
            size: "16px",
            weight: 400,
            fontFamily: "'Pancetta W05 Regular', 'Open Sans', 'Lato', sans-serif",
        },
        p: {
            size: "16px",
            weight: 400,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h1: {
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans', 'Lato', sans-serif",
            size: "40px",
            italic: true,
            weight: 1050,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h2: {
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans', 'Lato', sans-serif",
            size: "32px",
            italic: true,
            weight: 900,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h3: {
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans', 'Lato', sans-serif",
            size: "28px",
            italic: true,
            weight: 900,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h4: {
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans', 'Lato', sans-serif",
            size: "25px",
            italic: true,
            weight: 900,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h5: {
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans', 'Lato', sans-serif",
            size: "20px",
            italic: true,
            weight: 900,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h6: {
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans', 'Lato', sans-serif",
            size: "16px",
            italic: true,
            weight: 900,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        headerPrimary: {
            forwardAs: "h1",
            size: "24px",
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans SemiBold', 'Lato', sans-serif",
            italic: true,
            color: "text.main",
            transform: "uppercase",
            lineHeight: "normal",
            weight: 900,
        },
        headerSecondary: {
            forwardAs: "p",
            size: "16px",
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans SemiBold', 'Lato', sans-serif",
            italic: true,
            transform: "none",
            lineHeight: "normal",
            weight: 900,
        },
        headerTertiary: {
            forwardAs: "p",
            size: "15px",
            fontFamily: "'Pancetta W05 Black Italic', 'Open Sans SemiBold', 'Lato', sans-serif",
            italic: true,
            color: "text.main",
            transform: "uppercase",
            lineHeight: "normal",
            weight: 900,
        },
        legend: {
            forwardAs: "span",
            size: "10px",
            color: "text.accent",
            transform: "uppercase",
            weight: 400,
            css: css`
                margin-bottom: 0 !important;
            `,
        },
    },
    checkbox: {
        defaultProps: {
            css: css`
                cursor: pointer;
            `,
        },
    },
    breadcrumbs: {
        defaultProps: {
            typographyProps: {
                size: 14,
                weight: 400,
                fontFamily: "Pancetta, Pancetta W05 Regular",
            },
            css: css`
                font-size: 14px;
                color: #706f6f;
            `,
        },
    },
    button: {
        defaultProps: {
            sizeVariant: "medium",
        },
        primary: {
            color: "primary.main",
            buttonType: "outline",
            shape: "pill",
            typographyProps: {
                ...buttonTypographyProps,
                color: "primary.contrast",
            },
            css: css`
                border-color: #bdbcbc;

                svg {
                    color: currentColor;
                }
            `,
        },
        secondary: {
            color: "secondary.main",
            buttonType: "outline",
            shape: "pill",
            typographyProps: buttonTypographyProps,
            css: css`
                border-color: #bdbcbc;

                svg {
                    color: primary.main;
                }
            `,
        },
        tertiary: {
            color: "#0679E3",
            buttonType: "solid",
            shape: "pill",
            typographyProps: buttonTypographyProps,
        },
    },
    textField: {
        defaultProps: {
            cssOverrides: {
                formField: css`
                    margin-top: 4px;
                    margin-bottom: 16px;
                `,
                formInputWrapper: css`
                    border: none;
                    border-radius: 8px;

                    > input {
                        background-color: #ededed;

                        &:focus-within,
                        &:focus {
                            box-shadow: 0 0 8px 0 #e30613;
                        }
                    }
                `,
            },
            labelProps: {
                fontFamily: "Pancetta, Pancetta W05 Bold",
                weight: "bold",
                size: 16,
            },
            iconProps: {},
        },
    },
    pagination: {
        defaultProps: {},
    },
});
